import { useMemo } from "react";

import { getDataSourceStore } from "src/utils/devextreme-grid";
import { download, getExcelTemplateName } from "src/utils/page";
import api, { useSWR } from "src/utils/axios";

import { HOST_API } from "../config-global";

export const endpoint = "assets";
export const readingEndpoint = "assets/readings";

const baseUrl = `/api/${endpoint}/`;

export function changeParents(changes) {
  return api.put(`${baseUrl}change-parents`, { changes }, { showLoader: true });
}

/**
 * Removes locations and equipments in a single call, so a mixed selection is removed all or nothing.
 */
export function removeMultiple(ids, identifier) {
  return api.put(`${baseUrl}remove-multiple`, {
    ids,
    identifier,
  });
}

export function useGetAsset(id, type, undelete = false) {
  const baseUrl = `/api/${type}/`;

  const url = `${baseUrl}${undelete ? "get-undelete/" : ""}${id}`;

  const { data, isLoading, error, isValidating, mutate } = useSWR(id ? url : null);

  const memoizedValue = useMemo(
    () => ({
      asset: data ? { ...data } : null,
      isLoading,
      error,
      isValidating,
      mutate,
    }),
    [data, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}

export function useGetAssetDetail(id, type) {
  const baseUrl = `/api/${type}/`;

  const url = `${baseUrl}${id}/detail`;

  const { data, isLoading, error, isValidating, mutate } = useSWR(id ? url : null);

  const memoizedValue = useMemo(
    () => ({
      asset: data ? { ...data } : null,
      isLoading,
      error,
      isValidating,
      mutate,
    }),
    [data, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}

export function useGetPlainTree(shouldFetch = true, categoryId = null) {
  const url = `${baseUrl}plain-tree?categoryId=${categoryId}`;

  const { data, isLoading, error, isValidating, mutate } = useSWR(shouldFetch ? url : null);

  const memoizedValue = useMemo(
    () => ({
      tree: data || [],
      isLoading,
      error,
      isValidating,
      mutate,
    }),
    [data, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}

export function useGetAtachments(id, type) {
  const baseUrl = `/api/${type}/`;

  const url = `${baseUrl}${id}/attachments/gallery`;

  const { data, isLoading, error, isValidating, mutate } = useSWR(id ? url : null);

  const memoizedValue = useMemo(
    () => ({
      asset: data ? { ...data } : null,
      isLoading,
      error,
      isValidating,
      mutate,
    }),
    [data, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}

export function getGrid(typeId, locationId) {
  return getDataSourceStore(`${HOST_API}${baseUrl}get-grid/${typeId}/${locationId}`);
}

export function getList(id, search, typeId, locationId, componentId, meter, key, categoryId) {
  return api.post(`${baseUrl}get-list`, {
    id,
    search,
    typeId,
    locationId,
    componentId,
    meter,
    key,
    categoryId,
  });
}

export function getListByWorkOrder(search, workOrderId) {
  return api.post(`${baseUrl}get-list-by-work-order`, {
    search,
    workOrderId,
  });
}

export function getTreeList() {
  return api.get(`${baseUrl}get-tree-list`);
}

export function getReadingGrid(assetId, meterId, componentId) {
  return getDataSourceStore(
    `${HOST_API}${baseUrl}readings/get-grid/${assetId}/${meterId}/${componentId}`
  );
}

export function createReading(id, data) {
  return api.post(`${baseUrl}${id}/readings/`, data);
}

export function updateReading(id, readingId, data) {
  return api.put(`${baseUrl}${id}/readings/${readingId}/update`, data);
}

export function getGridFilter(filters) {
  return getDataSourceStore(`${HOST_API}${baseUrl}get-grid-list`, {}, filters);
}

export function removeReading(id, readingId, data) {
  console.log(data);
  return api.post(`${baseUrl}${id}/readings/${readingId}`, data);
}

export function useGetReading(id) {
  const url = `${baseUrl}readings/${id}`;

  const { data, isLoading, error, isValidating, mutate } = useSWR(id ? url : null);

  const memoizedValue = useMemo(
    () => ({
      reading: data || null,
      isLoading,
      error,
      isValidating,
      mutate,
    }),
    [data, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}

export function useGetReadingStats(assetId, meterId, enabled = true, excludeReadingId = null) {
  const shouldFetch = enabled && assetId && meterId;
  const url = excludeReadingId
    ? `${baseUrl}readings/stats/${assetId}/${meterId}?excludeReadingId=${excludeReadingId}`
    : `${baseUrl}readings/stats/${assetId}/${meterId}`;

  const { data, isLoading, error, isValidating, mutate } = useSWR(shouldFetch ? url : null);

  const memoizedValue = useMemo(
    () => ({
      stats: data || null,
      isLoading,
      error,
      isValidating,
      mutate,
    }),
    [data, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}

export async function downloadExcelTemplate() {
  const response = await api.get(`${baseUrl}download-excel-template/`, { responseType: "blob" });

  return download(response, getExcelTemplateName("assets"));
}

export async function downloadReadingsExcelTemplate() {
  const response = await api.get(`${baseUrl}readings/download-excel-template/`, {
    responseType: "blob",
  });

  download(response, getExcelTemplateName("readings"));
}
